/**
 * Corvenia Excel Add-in — Task Pane
 *
 * Quick setup: companies, master chart + mappings, trial balance → Raw_* + _Config.
 * Optional: “Download reference data” for currencies, FX, ownership, eliminations.
 */

/* global Office Excel */

import { CONFIG } from '../config/constants.js';
import { authService } from '../services/authService.js';
import { apiService } from '../services/apiService.js';
import { excelService, resolveFinancialReportFlags } from '../services/excelService.js';
import { collectWorkbookTablesForAi } from '../services/workbookContextService.js';
import { applyDashboardSpec } from '../services/dashboardSpecExecutor.js';

const APP_MODE_KEY = 'corvenia.taskpane.mode';
const MODE_LITE = 'lite';
const MODE_ADVANCED = 'advanced';
const MODE_AI = 'ai';
let litePartiesCache = [];

/** @type {{ isUser: boolean, content: string }[]} */
let aiDashboardChatHistory = [];
const AI_DASHBOARD_HISTORY_MAX = 14;

/** Worksheet name last built by this chat’s AI dashboard (refinements target this unless user asks for a new sheet). */
let lastAiDashboardSheetName = null;

/** User clearly wants a separate tab, not an in-place update of the session dashboard. */
function userWantsExplicitNewSheet(text) {
  return /\b(new\s+sheet|another\s+sheet|another\s+tab|separate\s+sheet|separate\s+tab|fresh\s+sheet|duplicate\s+(this|the)?\s*dashboard|copy\s+to\s+(a\s+)?new|create\s+(a\s+)?new\s+(sheet|tab|worksheet)|brand[\s-]new\s+sheet|do\s+not\s+update|don'?t\s+update|start\s+over\s+on\s+(a\s+)?new)\b/i.test(
    String(text || '')
  );
}

function buildPartyNameByIdFromApi(parties) {
  const lookup = {};
  for (const p of parties) {
    const id = Number(p.partyId ?? p.PartyId ?? 0);
    const name = String(p.name ?? p.Name ?? p.partyName ?? '').trim();
    if (id > 0 && name) lookup[id] = name;
  }
  return lookup;
}

/** Must match `replaceTableData` / GetTrialBalanceFlat mapping everywhere we write tbl_RawTrialBalance. */
const RAW_TRIAL_BALANCE_HEADERS = [
  'PartyId', 'PartyName', 'PartyAccountNumber', 'PartyAccountName',
  'Year', 'Month', 'MonthName', 'AccountNumber', 'AccountName', 'AccountClass',
  'Currency', 'BalanceChange', 'BalanceIn', 'BalanceOut', 'EliminationsApplied',
];

/** @param {object} r API row @param {Record<number, string>} [partyNameById] */
function mapTrialBalanceApiRow(r, partyNameById = {}) {
  const partyId = r.partyId ?? '';
  const partyName =
    String(r.partyName ?? '').trim()
    || partyNameById[Number(partyId)]
    || partyNameById[partyId]
    || '';
  return [
    partyId, partyName, r.partyAccountNumber ?? '', r.partyAccountName ?? '',
    r.year ?? '', r.month ?? '', r.monthName ?? '',
    r.masterAccountNumber ?? r.accountNumber ?? '', r.masterAccountName ?? r.accountName ?? '',
    r.accountClass ?? '', r.currencyCode ?? '', r.balanceChange ?? 0, r.balanceIn ?? 0, r.balanceOut ?? 0, false,
  ];
}

/** Lite mode — two actions (copy kept in sync with taskpane.html button labels). */
const LITE_BTN_REFRESH = 'Refresh reports';
const LITE_BTN_DOWNLOAD = 'Get latest data';

// ── Bootstrap ─────────────────────────────────────────────────────────────────

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.Excel) return;

  document.getElementById('sideload-msg').style.display = 'none';
  document.getElementById('app-body').style.display     = 'flex';

  checkAuthState();
  wireButtons();

  // If _Config already exists, register the Sync button click handler
  try { await excelService.registerSyncButton(handleSyncFromRawData); } catch {}
  try { await refreshSyncScopePreview(); } catch { /* no workbook yet */ }
});

// ── Auth state ────────────────────────────────────────────────────────────────

async function checkAuthState() {
  try {
    const result = await authService.checkAuthenticationState();
    if (result.isAuthenticated) showAuthenticated();
    else                        showUnauthenticated();
  } catch {
    showUnauthenticated();
  }
}

function showAuthenticated() {
  document.getElementById('login-section').style.display  = 'none';
  document.getElementById('main-content').style.display   = 'block';
  document.getElementById('sign-out-btn').style.display   = 'inline-block';
  try {
    const username = authService.getUsername?.() || '';
    const el = document.getElementById('user-email');
    if (el && username && username !== 'User') el.textContent = username;
  } catch { /* non-critical */ }
  refreshSyncScopePreview().catch(() => {});
  initLiteMode().catch(() => {});
}

function showUnauthenticated() {
  document.getElementById('login-section').style.display  = 'flex';
  document.getElementById('main-content').style.display   = 'none';
  document.getElementById('sign-out-btn').style.display   = 'none';
}

// ── Button wiring ─────────────────────────────────────────────────────────────

function wireButtons() {
  document.getElementById('login-btn')?.addEventListener('click', handleLogin);
  document.getElementById('sign-out-btn')?.addEventListener('click', handleSignOut);
  document.getElementById('sync-data-btn')?.addEventListener('click', handleSyncData);
  document.getElementById('setup-workbook-btn')?.addEventListener('click', handleSetupWorkbook);
  document.getElementById('download-reference-btn')?.addEventListener('click', handleDownloadReferenceData);
  document.getElementById('open-config-btn')?.addEventListener('click', handleOpenConfigSheet);
  document.getElementById('refresh-scope-preview-btn')?.addEventListener('click', () => {
    refreshSyncScopePreview().catch(() => {});
  });
  document.getElementById('mode-lite-btn')?.addEventListener('click', () => setAppMode(MODE_LITE));
  document.getElementById('mode-advanced-btn')?.addEventListener('click', () => setAppMode(MODE_ADVANCED));
  document.getElementById('mode-ai-btn')?.addEventListener('click', () => setAppMode(MODE_AI));
  document.getElementById('ai-get-latest-data-btn')?.addEventListener('click', () => {
    handleLiteSyncWarehouse()
      .then(() => updateAiAssistanceReadinessBanner().catch(() => {}))
      .catch(() => {});
  });
  document.getElementById('lite-refresh-parties-btn')?.addEventListener('click', () => {
    loadLiteParties(true).catch(() => {});
  });
  document.getElementById('lite-frequency-select')?.addEventListener('change', () => {
    refreshLitePeriodOptions();
  });
  document.getElementById('lite-sync-report-btn')?.addEventListener('click', () => {
    handleLiteSyncReport().catch(() => {});
  });
  document.getElementById('lite-sync-warehouse-btn')?.addEventListener('click', () => {
    handleLiteSyncWarehouse().catch(() => {});
  });
  document.getElementById('ai-chat-send')?.addEventListener('click', () => {
    handleAiDashboardChatSend().catch((e) => console.error('[AI chat]', e));
  });
  document.getElementById('ai-chat-input')?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      handleAiDashboardChatSend().catch((e) => console.error('[AI chat]', e));
    }
  });
}

function setAppMode(mode) {
  let safeMode = MODE_LITE;
  if (mode === MODE_ADVANCED) safeMode = MODE_ADVANCED;
  else if (mode === MODE_AI) safeMode = MODE_AI;
  try { localStorage.setItem(APP_MODE_KEY, safeMode); } catch { /* ignore */ }
  const isLite = safeMode === MODE_LITE;
  const isAdvanced = safeMode === MODE_ADVANCED;
  const isAi = safeMode === MODE_AI;
  const litePanel = document.getElementById('lite-mode-panel');
  const advPanel = document.getElementById('advanced-mode-panels');
  const aiPanel = document.getElementById('ai-assistance-panel');
  const liteBtn = document.getElementById('mode-lite-btn');
  const advBtn = document.getElementById('mode-advanced-btn');
  const aiBtn = document.getElementById('mode-ai-btn');
  if (litePanel) litePanel.style.display = isLite ? 'block' : 'none';
  if (advPanel) advPanel.style.display = isAdvanced ? 'block' : 'none';
  if (aiPanel) aiPanel.style.display = isAi ? 'block' : 'none';
  if (liteBtn) liteBtn.classList.toggle('active', isLite);
  if (advBtn) advBtn.classList.toggle('active', isAdvanced);
  if (aiBtn) aiBtn.classList.toggle('active', isAi);
  if (isAi) updateAiAssistanceReadinessBanner().catch(() => {});
}

async function initLiteMode() {
  const storedMode = (() => {
    return MODE_LITE;
  })();
  setAppMode(storedMode);

  const yearSel = document.getElementById('lite-year-select');
  if (yearSel && yearSel.options.length === 0) {
    const currentYear = new Date().getFullYear();
    const startYear = CONFIG.DATE.START_YEAR || 2020;
    for (let y = currentYear; y >= startYear; y--) {
      const opt = document.createElement('option');
      opt.value = String(y);
      opt.textContent = String(y);
      yearSel.appendChild(opt);
    }
    yearSel.value = String(currentYear);
  }

  await loadLiteParties(false);
  await loadLiteProfiles();
  refreshLitePeriodOptions();
}

function freqLabelToPeriodType(label) {
  const map = { Monthly: 1, Quarterly: 3, 'Half-year': 6, 'Full year': 12 };
  return map[label] || 12;
}

function refreshLitePeriodOptions() {
  const freq = document.getElementById('lite-frequency-select')?.value || 'Full year';
  const sel = document.getElementById('lite-period-select');
  if (!sel) return;
  const MONTH_NAMES = CONFIG.DATE.MONTH_NAMES || [];
  sel.innerHTML = '';
  if (freq === 'Full year') {
    const o = document.createElement('option');
    o.value = '0';
    o.textContent = 'Full year';
    sel.appendChild(o);
  } else if (freq === 'Monthly') {
    const o0 = document.createElement('option');
    o0.value = '0';
    o0.textContent = 'All months';
    sel.appendChild(o0);
    for (let m = 1; m <= 12; m++) {
      const o = document.createElement('option');
      o.value = String(m);
      o.textContent = MONTH_NAMES[m - 1] || `Month ${m}`;
      sel.appendChild(o);
    }
  } else if (freq === 'Quarterly') {
    for (let q = 1; q <= 4; q++) {
      const o = document.createElement('option');
      o.value = String(q);
      o.textContent = `Q${q}`;
      sel.appendChild(o);
    }
  } else if (freq === 'Half-year') {
    const o1 = document.createElement('option');
    o1.value = '1';
    o1.textContent = 'H1';
    sel.appendChild(o1);
    const o2 = document.createElement('option');
    o2.value = '2';
    o2.textContent = 'H2';
    sel.appendChild(o2);
  }
}

async function loadLiteProfiles() {
  const sel = document.getElementById('lite-profile-select');
  if (!sel || sel.options.length > 0) return;
  try {
    const profiles = await apiService.fetchMasterProfiles();
    sel.innerHTML = '';
    for (const p of profiles) {
      const opt = document.createElement('option');
      opt.value = String(p.profileId ?? p.ProfileId ?? 1);
      opt.textContent = String(p.name ?? p.Name ?? opt.value);
      sel.appendChild(opt);
    }
    if (sel.options.length === 0) {
      const o = document.createElement('option');
      o.value = '1';
      o.textContent = '1';
      sel.appendChild(o);
    }
  } catch {
    const o = document.createElement('option');
    o.value = '1';
    o.textContent = '1';
    sel.appendChild(o);
  }
}

async function loadLiteParties(forceRefresh = false) {
  const sel = document.getElementById('lite-company-select');
  if (!sel) return;
  if (!forceRefresh && litePartiesCache.length > 0) return;

  let parties = [];
  try {
    parties = await apiService.fetchPartiesDetail();
  } catch {
    litePartiesCache = [];
    sel.innerHTML = '';
    const o = document.createElement('option');
    o.value = '';
    o.textContent = 'All companies';
    sel.appendChild(o);
    return;
  }

  litePartiesCache = parties.map(p => ({
    partyId: Number(p.partyId ?? p.PartyId ?? 0),
    name: String(p.name ?? p.Name ?? '')
  })).filter(p => p.partyId > 0);

  const prev = sel.value;
  sel.innerHTML = '';
  const allOpt = document.createElement('option');
  allOpt.value = '';
  allOpt.textContent = 'All companies';
  sel.appendChild(allOpt);
  for (const p of litePartiesCache.sort((a, b) => a.name.localeCompare(b.name))) {
    const opt = document.createElement('option');
    opt.value = String(p.partyId);
    opt.textContent = `${p.name} (${p.partyId})`;
    sel.appendChild(opt);
  }
  if (prev && [...sel.options].some(o => o.value === prev)) sel.value = prev;
}

async function resolveLitePartyIds() {
  const sel = document.getElementById('lite-company-select');
  if (sel?.value) {
    const n = Number(sel.value);
    return n > 0 ? [n] : [];
  }
  let ids = litePartiesCache.map(p => p.partyId);
  if (ids.length === 0) {
    try {
      ids = await excelService.readPartyIdsFromRawTable();
    } catch { /* ignore */ }
  }
  return ids;
}

function buildLiteSyncConfigPartial() {
  const year = Number(document.getElementById('lite-year-select')?.value || new Date().getFullYear());
  const freqStr = document.getElementById('lite-frequency-select')?.value || 'Full year';
  const periodType = freqLabelToPeriodType(freqStr);
  const period = Number(document.getElementById('lite-period-select')?.value ?? 0);
  const reportPack = String(document.getElementById('lite-report-pack-select')?.value || 'Trial balance only');
  const allFinancialReports = /all\s*financial/i.test(reportPack);
  const numsStr = document.getElementById('lite-numbers-select')?.value || 'Whole';
  const numsMap = { Whole: 0, Thousands: 1, Millions: 2 };
  const numbersIn = numsMap[numsStr] ?? 0;
  const profileId = Number(document.getElementById('lite-profile-select')?.value || 1);
  return {
    year,
    periodType,
    period,
    profileId,
    numbersIn,
    reportCurrency: null,
    syncScope: 'TrialBalance',
    reportPack,
    allFinancialReports,
    includeCompanyElimination: false,
    includeHistoricalData: false,
    groupEntityId: null
  };
}

async function buildLiteSyncConfig() {
  const partyIds = await resolveLitePartyIds();
  return { ...buildLiteSyncConfigPartial(), partyIds };
}

function setLiteSummaryLine(kind, detail) {
  const el = document.getElementById('lite-sync-summary');
  if (!el) return;
  const t = new Date().toLocaleString();
  el.textContent = kind === 'warehouse'
    ? `${t} · Latest data downloaded · ${detail}`
    : `${t} · Reports refreshed · ${detail}`;
}

function setLiteBusy(action, busy) {
  const reportBtn = document.getElementById('lite-sync-report-btn');
  const warehouseBtn = document.getElementById('lite-sync-warehouse-btn');
  const aiWarehouseBtn = document.getElementById('ai-get-latest-data-btn');
  const runStatus = document.getElementById('lite-run-status');
  if (!reportBtn || !warehouseBtn) return;

  if (busy) {
    reportBtn.disabled = true;
    warehouseBtn.disabled = true;
    if (aiWarehouseBtn) aiWarehouseBtn.disabled = true;
    reportBtn.textContent = action === 'report' ? 'Refreshing reports…' : LITE_BTN_REFRESH;
    warehouseBtn.textContent = action === 'warehouse' ? 'Downloading latest data…' : LITE_BTN_DOWNLOAD;
    if (runStatus) {
      runStatus.textContent = action === 'warehouse'
        ? 'Downloading from Corvenia… this can take a minute.'
        : 'Updating your report sheets from saved data…';
      runStatus.style.display = 'block';
    }
  } else {
    reportBtn.disabled = false;
    warehouseBtn.disabled = false;
    if (aiWarehouseBtn) aiWarehouseBtn.disabled = false;
    reportBtn.textContent = LITE_BTN_REFRESH;
    warehouseBtn.textContent = LITE_BTN_DOWNLOAD;
    if (runStatus) runStatus.style.display = 'none';
  }
}

async function handleLiteSyncReport() {
  setLiteBusy('report', true);
  setStatus('', '');
  await excelService.setConfigStatusMessage('Refreshing reports…', { loading: true });
  try {
    setProgress('Refreshing reports…');
    const cfg = await buildLiteSyncConfig();
    if (!cfg.partyIds || cfg.partyIds.length === 0) {
      setStatus('No saved data yet. Click “Get latest data” once to download numbers from Corvenia, or pick a company above.', 'error');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }
    await excelService.syncFromRawData(cfg);
    await excelService.updateLastSyncTime();
    setStatus('Reports updated from saved data.', 'success');
    const rf = resolveFinancialReportFlags(cfg);
    const repParts = [];
    if (rf.profitLoss) repParts.push('P&L');
    if (rf.balanceSheet) repParts.push('BS');
    if (rf.cashFlow) repParts.push('CF');
    const rep = repParts.length > 0 ? repParts.join('+') : 'TB';
    setLiteSummaryLine('report', `Year ${cfg.year} · ${rep}`);
    await excelService.setConfigStatusMessage('Ready', { loading: false });
  } catch (err) {
    console.error('[LiteSyncReport]', err);
    setStatus(err.message || 'Update failed', 'error');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
  } finally {
    setLiteBusy('report', false);
    setProgress('');
  }
}

async function handleLiteSyncWarehouse() {
  setLiteBusy('warehouse', true);
  setStatus('', '');
  await excelService.setConfigStatusMessage('Downloading latest data…', { loading: true });

  try {
    setProgress('Signing in…');
    let token;
    try { token = await authService.getAccessToken(); } catch { token = null; }
    if (!token) {
      setStatus('Sign in required.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    await loadLiteParties(false);
    await loadLiteProfiles();

    const cfgPartial = buildLiteSyncConfigPartial();
    const partyIds = await resolveLitePartyIds();
    if (partyIds.length === 0) {
      setStatus('No companies.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    const selectedPartySet = new Set(partyIds);
    const selectedParties = litePartiesCache.filter(p => selectedPartySet.has(p.partyId));
    const partiesForTable = selectedParties.length > 0
      ? selectedParties
      : partyIds.map(id => ({ partyId: id, name: '' }));

    const year = cfgPartial.year;
    const profileId = cfgPartial.profileId;
    const startYear = CONFIG.DATE.START_YEAR || 2020;
    const writeTable = excelService.replaceTableData.bind(excelService);

    setProgress('Saving companies…');
    await excelService.setConfigStatusMessage('Saving companies…', { loading: true });
    await writeTable(
      CONFIG.SHEETS.RAW_PARTIES, 'tbl_RawParties',
      ['PartyId','Name','OrgNumber','CountryCode','CompanyType','EnableSync','StartDate','EndDate'],
      partiesForTable.map(p => [p.partyId, p.name, '', '', '', '', '', ''])
    );

    setProgress('Downloading chart…');
    await excelService.setConfigStatusMessage('Downloading chart…', { loading: true });
    const [profiles, accounts, mappings] = await Promise.all([
      apiService.fetchMasterProfiles(),
      apiService.fetchMasterAccounts(),
      apiService.fetchAccountMappings()
    ]);
    if (profiles.length > 0) {
      await writeTable(CONFIG.SHEETS.RAW_MASTER_PROFILES, 'tbl_RawMasterProfiles', ['ProfileId','Name'],
        profiles.map(p => [p.profileId ?? '', p.name ?? '']));
    }
    if (accounts.length > 0) {
      await writeTable(CONFIG.SHEETS.RAW_MASTER_ACCOUNTS, 'tbl_RawMasterAccounts',
        ['ProfileId','ProfileName','AccountId','AccountNumber','AccountName','ParentAccountId','Statement','Section','NormalBalance','CashFlowClass','IsCash','IsContra'],
        accounts.map(a => [a.profileId ?? '', a.profileName ?? '', a.accountId ?? '', a.accountNumber ?? '', a.accountName ?? '', a.parentAccountId ?? '', a.statement ?? '', a.section ?? '', a.normalBalance ?? '', a.cashFlowClass ?? '', a.isCash ?? false, a.isContra ?? false]));
    }
    if (mappings.length > 0) {
      await writeTable(CONFIG.SHEETS.RAW_ACCOUNT_MAPPINGS, 'tbl_RawAccountMappings',
        ['PartyId','ProfileId','PartyAccountNumber','PartyAccountName','MasterAccountId','MasterAccountNumber','MasterAccountName'],
        mappings.map(m => [m.partyId ?? '', m.profileId ?? '', m.partyAccountNumber ?? '', m.partyAccountName ?? '', m.masterAccountId ?? '', m.masterAccountNumber ?? '', m.masterAccountName ?? '']));
    }

    setProgress('Downloading balances…');
    const allTbRows = [];
    for (let yr = startYear; yr <= year; yr++) {
      await excelService.setConfigStatusMessage(`Downloading ${yr}…`, { loading: true });
      const tbResp = await apiService.fetchTrialBalanceFlat({
        partyIds,
        year: yr,
        periodType: 12,
        period: 0,
        profileId,
        includeCompanyElimination: false,
        includeHistoricalData: false,
        partyAccountGrain: true
      });
      const rows = Array.isArray(tbResp) ? tbResp : (tbResp.rows ?? []);
      for (const r of rows) allTbRows.push(r);
    }
    await writeTable(
      CONFIG.SHEETS.RAW_TRIAL_BALANCE,
      'tbl_RawTrialBalance',
      RAW_TRIAL_BALANCE_HEADERS,
      allTbRows.map(mapTrialBalanceApiRow)
    );

    setProgress('Building reports…');
    await excelService.setConfigStatusMessage('Building reports…', { loading: true });
    const cfg = { ...cfgPartial, partyIds };
    await excelService.syncFromRawData(cfg);
    await excelService.updateLastSyncTime();

    setStatus('Latest data downloaded and reports updated.', 'success');
    setLiteSummaryLine('warehouse', `${partyIds.length} companies · ${allTbRows.length} rows`);
    setProgress('');
    await excelService.setConfigStatusMessage('Ready', { loading: false });
    updateAiAssistanceReadinessBanner().catch(() => {});
  } catch (err) {
    console.error('[LiteSyncWarehouse]', err);
    setStatus(err.message || 'Sync failed', 'error');
    setProgress('');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
  } finally {
    setLiteBusy('warehouse', false);
    setProgress('');
  }
}

/** Matches handleSyncData branching — what “Re-download” will fetch for the chosen Sync Scope */
function describeSyncScopePlan(scopeRaw) {
  const s = String(scopeRaw || 'TrialBalance').trim().toLowerCase();
  if (s === 'all') {
    return [
      'Companies (Raw_Parties)',
      'Currencies (Raw_Currencies)',
      'Exchange rates (Raw_ExchangeRates)',
      'Ownership (Raw_Ownership)',
      'Master chart + mappings',
      'Trial balance (Raw_TrialBalance)',
      'Eliminations (Raw_Eliminations)'
    ];
  }
  if (s === 'trialbalance') return ['Trial balance only (Raw_TrialBalance)'];
  if (s === 'parties') return ['Companies (Raw_Parties)', 'Currencies list (Raw_Currencies)'];
  if (s === 'currencies') {
    return [
      'Companies (Raw_Parties)',
      'Currencies (Raw_Currencies)',
      'Exchange rates (Raw_ExchangeRates)'
    ];
  }
  if (s === 'ownership') return ['Group ownership (Raw_Ownership)'];
  if (s === 'masterchart') return ['Master profiles, accounts, mappings'];
  if (s === 'eliminations') return ['Intercompany eliminations (Raw_Eliminations)'];
  return [`Scope “${scopeRaw}” — re-download uses API filters for this key`];
}

async function refreshSyncScopePreview() {
  const el = document.getElementById('sync-scope-preview');
  if (!el) return;
  try {
    const cfg = await excelService.readConfigValues();
    const scopeLabel = (cfg.syncScope || 'TrialBalance').toString().trim();
    const plan = describeSyncScopePlan(scopeLabel);
    const reportPack = (cfg.reportPack || 'Trial balance only').toString().trim();
    const lines = plan.map((t) => `<li>${t}</li>`).join('');
    el.innerHTML = `
      <div class="scope-current"><strong>Sync Scope on _Config:</strong> ${scopeLabel}</div>
      <div class="scope-current"><strong>Reports to build:</strong> ${reportPack}</div>
      <ol class="scope-plan">${lines}</ol>
      <p class="scope-hint">Re-download only pulls the rows above. Use <strong>All</strong> on _Config only when you need a full refresh.</p>`;
  } catch {
    el.innerHTML = '<p class="scope-muted">Create <strong>_Config</strong> with Setup Workbook, then choose <strong>Sync Scope</strong> before re-downloading.</p>';
  }
}

async function handleLogin() {
  try {
    setStatus('Signing in…', 'info');
    const result = await authService.checkAuthenticationState();
    if (result.isAuthenticated) { showAuthenticated(); setStatus(''); }
    else setStatus(result.message || 'Could not sign in. Make sure you are signed in to Microsoft Office.', 'error');
  } catch (err) {
    setStatus(`Sign-in failed: ${err.message}`, 'error');
  }
}

async function handleSignOut() {
  authService.clearAuth();
  showUnauthenticated();
}

// ── Quick setup: parties + master + TB (reference data via separate button) ─

async function handleSetupWorkbook() {
  const btn = document.getElementById('setup-workbook-btn');
  if (btn) btn.disabled = true;
  setStatus('', '');
  await excelService.setConfigStatusMessage('Setting up…', { loading: true });

  try {
    setProgress('Checking authentication…');
    let token;
    try { token = await authService.getAccessToken(); } catch { token = null; }
    if (!token) {
      setStatus('Please sign in first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    const currentYear = new Date().getFullYear();
    const stats = {};
    let currencyCodes = [];
    let groupEntities = [];
    let profiles = [];

    setProgress('Step 1/4 — Fetching companies and reference lists…');
    let parties = [];
    try {
      parties = await apiService.fetchPartiesDetail();
      stats.parties = parties.length;
    } catch (e) {
      console.warn('[Setup] fetchPartiesDetail failed:', e.message);
    }

    try {
      const [currencies, groups] = await Promise.all([
        apiService.fetchCurrencies().catch(() => []),
        apiService.fetchGroupEntities().catch(() => []),
      ]);
      currencyCodes = (currencies || []).map((c) => c.code ?? c.Code).filter(Boolean);
      groupEntities = Array.isArray(groups) ? groups : [];
    } catch (e) {
      console.warn('[Setup] currencies / group entities:', e.message);
    }

    const allPartyIds = parties
      .map(p => p.partyId ?? p.PartyId)
      .filter(id => id != null);

    if (parties.length > 0) {
      setProgress(`Step 1/4 — Writing ${parties.length} companies…`);
      await excelService.replaceTableData(
        CONFIG.SHEETS.RAW_PARTIES, 'tbl_RawParties',
        ['PartyId','Name','OrgNumber','CountryCode','CompanyType','EnableSync','StartDate','EndDate'],
        parties.map(p => [
          p.partyId ?? '', p.name ?? '', p.orgNumber ?? '', p.countryCode ?? '',
          p.companyType ?? '', p.enableSync ?? '', p.startDate ?? '', p.endDate ?? ''
        ])
      );
    }

    setProgress('Step 2/4 — Fetching master chart and mappings…');
    try {
      const [profilesResp, accounts, mappings] = await Promise.all([
        apiService.fetchMasterProfiles(),
        apiService.fetchMasterAccounts(),
        apiService.fetchAccountMappings()
      ]);
      profiles = profilesResp;
      stats.profiles = profiles.length;
      stats.accounts = accounts.length;
      stats.mappings = mappings.length;

      if (profiles.length > 0) {
        await excelService.replaceTableData(
          CONFIG.SHEETS.RAW_MASTER_PROFILES, 'tbl_RawMasterProfiles',
          ['ProfileId','Name'],
          profiles.map(p => [p.profileId ?? '', p.name ?? ''])
        );
      }
      if (accounts.length > 0) {
        await excelService.replaceTableData(
          CONFIG.SHEETS.RAW_MASTER_ACCOUNTS, 'tbl_RawMasterAccounts',
          ['ProfileId','ProfileName','AccountId','AccountNumber','AccountName',
           'ParentAccountId','Statement','Section','NormalBalance','CashFlowClass','IsCash','IsContra'],
          accounts.map(a => [
            a.profileId ?? '', a.profileName ?? '', a.accountId ?? '',
            a.accountNumber ?? '', a.accountName ?? '', a.parentAccountId ?? '',
            a.statement ?? '', a.section ?? '', a.normalBalance ?? '', a.cashFlowClass ?? '',
            a.isCash ?? false, a.isContra ?? false
          ])
        );
      }
      if (mappings.length > 0) {
        await excelService.replaceTableData(
          CONFIG.SHEETS.RAW_ACCOUNT_MAPPINGS, 'tbl_RawAccountMappings',
          ['PartyId','ProfileId','PartyAccountNumber','PartyAccountName','MasterAccountId','MasterAccountNumber','MasterAccountName'],
          mappings.map(m => [
            m.partyId ?? '', m.profileId ?? '', m.partyAccountNumber ?? '', m.partyAccountName ?? '',
            m.masterAccountId ?? '', m.masterAccountNumber ?? '', m.masterAccountName ?? ''
          ])
        );
      }
    } catch (e) { console.warn('[Setup] master chart failed:', e.message); }

    if (allPartyIds.length > 0) {
      const startYear = CONFIG.DATE.START_YEAR || 2020;
      const allTbRows = [];
      for (let yr = startYear; yr <= currentYear; yr++) {
        setProgress(`Step 3/4 — Trial balance ${yr} (${allPartyIds.length} companies)…`);
        await excelService.setConfigStatusMessage(`Downloading trial balance ${yr}…`, { loading: true });
        try {
          const tbResp = await apiService.fetchTrialBalanceFlat({
            partyIds: allPartyIds,
            year: yr,
            periodType: 12,
            period: 0,
            profileId: 1,
            includeCompanyElimination: false,
            includeHistoricalData: false,
            partyAccountGrain: true
          });
          const rows = Array.isArray(tbResp) ? tbResp : (tbResp.rows ?? []);
          for (const r of rows) allTbRows.push(r);
        } catch (e) {
          console.warn(`[Setup] TB year ${yr} failed:`, e.message);
        }
      }
      stats.trialBalance = allTbRows.length;
      setProgress(`Step 3/4 — Writing trial balance (${allTbRows.length} rows)…`);
      const partyNameById = buildPartyNameByIdFromApi(parties);
      await excelService.replaceTableData(
        CONFIG.SHEETS.RAW_TRIAL_BALANCE,
        'tbl_RawTrialBalance',
        RAW_TRIAL_BALANCE_HEADERS,
        allTbRows.map((r) => mapTrialBalanceApiRow(r, partyNameById))
      );
    } else {
      console.warn('[Setup] No party IDs found — skipping trial balance.');
    }

    let configExists = false;
    try {
      await Excel.run(async (ctx) => {
        const s = ctx.workbook.worksheets.getItemOrNullObject(CONFIG.SHEETS.CONFIG_SHEET);
        s.load('isNullObject');
        await ctx.sync();
        configExists = !s.isNullObject;
      });
    } catch { /* ignore */ }

    if (!configExists) {
      setProgress('Step 4/4 — Building _Config…');
      const partyList = parties.map(p => ({ partyId: p.partyId ?? p.PartyId, name: p.name ?? p.Name }));
      const profileOpts = profiles.map(p => ({
        profileId: p.profileId ?? p.ProfileId,
        name: p.name ?? p.Name,
      }));
      await excelService.buildControlSheet(currentYear, partyList, currencyCodes, allPartyIds, {
        groupEntities,
        profiles: profileOpts,
      });
    }

    await excelService.updateLastSyncTime();

    setProgress('Building trial balance report from raw data…');
    await excelService.setConfigStatusMessage('Building reports…', { loading: true });
    try {
      const cfg = await excelService.readConfigValues();
      await excelService.syncFromRawData(cfg);
      await excelService.updateLastSyncTime();
    } catch (e) {
      console.warn('[Setup] auto-sync after setup:', e.message);
    }

    setProgress('');
    await excelService.setConfigStatusMessage('Ready', { loading: false });
    await refreshSyncScopePreview();

    const parts = [
      stats.parties ? `${stats.parties} companies` : null,
      stats.trialBalance ? `${stats.trialBalance} TB rows` : null,
      stats.accounts ? `${stats.accounts} master accts` : null,
    ].filter(Boolean);

    setStatus(`Setup complete — ${parts.join(' · ')}. Use “Download reference data” for currencies and more.`, 'success');

    await excelService.registerSyncButton(handleSyncFromRawData);
    await navigateToConfig();

  } catch (err) {
    console.error('[SetupWorkbook]', err);
    setProgress('');
    setStatus(`Error: ${err.message}`, 'error');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function handleDownloadReferenceData() {
  const btn = document.getElementById('download-reference-btn');
  if (btn) btn.disabled = true;
  setStatus('', '');
  await excelService.setConfigStatusMessage('Downloading reference data…', { loading: true });

  try {
    setProgress('Checking authentication…');
    let token;
    try { token = await authService.getAccessToken(); } catch { token = null; }
    if (!token) {
      setStatus('Please sign in first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    let cfg;
    try {
      cfg = await excelService.readConfigValues();
    } catch {
      setStatus('_Config not found. Run Setup Workbook first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    const currentYear = new Date().getFullYear();
    let partyIds = cfg.partyIds?.length > 0 ? cfg.partyIds : null;
    if (!partyIds) {
      try {
        partyIds = await excelService.readPartyIdsFromRawTable();
      } catch { /* ignore */ }
    }

    const writeTable = excelService.replaceTableData.bind(excelService);

    setProgress('Fetching currencies…');
    try {
      const currencies = await apiService.fetchCurrencies();
      if (currencies.length > 0) {
        await writeTable(CONFIG.SHEETS.RAW_CURRENCIES, 'tbl_RawCurrencies',
          ['CurrencyId','Code','DisplayName','Symbol','NumericCode'],
          currencies.map(c => [c.id ?? '', c.code ?? '', c.displayName ?? '', c.symbol ?? '', c.numericCode ?? '']));
      }
    } catch (e) { console.warn('[RefData] currencies:', e.message); }

    setProgress('Fetching exchange rates…');
    try {
      const ratesResp = await apiService.fetchExchangeRates({ limit: 50000 });
      const rateArr = Array.isArray(ratesResp) ? ratesResp : (ratesResp.rows ?? ratesResp.items ?? []);
      if (rateArr.length > 0) {
        await writeTable(CONFIG.SHEETS.RAW_EXCHANGE_RATES, 'tbl_RawExchangeRates',
          ['FromCurrency','ToCurrency','Rate','EffectiveDate'],
          rateArr.map(r => [r.fromCurrencyCode ?? '', r.toCurrencyCode ?? '', r.rate ?? 0, r.effectiveDate ?? '']));
      }
    } catch (e) { console.warn('[RefData] rates:', e.message); }

    setProgress('Fetching ownership…');
    try {
      const ownerships = await apiService.fetchPartyOwnerships();
      if (ownerships.length > 0) {
        await writeTable(CONFIG.SHEETS.RAW_OWNERSHIP, 'tbl_RawOwnership',
          ['OwnedPartyId','OwnedPartyName','OwnerPartyId','OwnerPartyName','OwnershipPct','OwnershipType','EffectiveFrom','EffectiveTo'],
          ownerships.map(o => [
            o.ownedPartyId ?? '', o.ownedPartyName ?? '', o.ownerPartyId ?? '', o.ownerPartyName ?? '',
            o.ownershipPercentage ?? '', o.ownershipType ?? '', o.effectiveFrom ?? '', o.effectiveTo ?? ''
          ]));
      }
    } catch (e) { console.warn('[RefData] ownership:', e.message); }

    if (partyIds && partyIds.length > 0) {
      setProgress('Fetching eliminations…');
      try {
        const elimRows = await apiService.fetchIntercompanyEliminations(
          partyIds, currentYear, 1, currentYear, 12
        );
        if (elimRows.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_ELIMINATIONS, 'tbl_RawEliminations',
            ['EliminationId','PartyId','PartyName','ReciprocalPartyId','ReciprocalPartyName',
             'Year','Month','AccountNumber','Amount','CurrencyId','Status'],
            elimRows.map(r => [
              r.eliminationId ?? '', r.partyId ?? '', r.partyName ?? '',
              r.reciprocalPartyId ?? '', r.reciprocalPartyName ?? '',
              r.year ?? '', r.month ?? '', r.accountNumber ?? '',
              r.amount ?? 0, r.currencyId ?? '', r.status ?? ''
            ]));
        }
      } catch (e) { console.warn('[RefData] eliminations:', e.message); }
    }

    await excelService.updateLastSyncTime();
    setProgress('');
    await excelService.setConfigStatusMessage('Ready', { loading: false });
    setStatus('Reference data downloaded (currencies, rates, ownership, eliminations where available).', 'success');
    try {
      await excelService.refreshConfigDerivedValidation();
    } catch (e) {
      console.warn('[RefData] refresh _Config dropdowns:', e.message);
    }
    await refreshSyncScopePreview();
  } catch (err) {
    console.error('[DownloadReferenceData]', err);
    setProgress('');
    setStatus(`Error: ${err.message}`, 'error');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
  } finally {
    if (btn) btn.disabled = false;
  }
}

// ── Sync from Raw Data (reads _Config + Raw_* tables, builds Report sheet) ───

async function handleSyncFromRawData() {
  setStatus('', '');
  setProgress('Reading filters from _Config…');
  await excelService.setConfigStatusMessage('Building reports…', { loading: true });
  try {
    let cfg;
    try { cfg = await excelService.readConfigValues(); }
    catch {
      setStatus('_Config sheet not found. Click "Setup Workbook" first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    setProgress('Filtering raw data and building report…');
    await excelService.syncFromRawData(cfg);
    await excelService.updateLastSyncTime();
    setProgress('');
    await excelService.setConfigStatusMessage('Ready', { loading: false });
    setStatus('Report updated from raw data.', 'success');
  } catch (err) {
    console.error('[SyncFromRaw]', err);
    setProgress('');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
    setStatus(`Error: ${err.message}`, 'error');
  }
}

// ── Re-download raw data from API ────────────────────────────────────────────

async function handleSyncData() {
  const btn = document.getElementById('sync-data-btn');
  if (btn) btn.disabled = true;
  setStatus('', '');
  await refreshSyncScopePreview();
  await excelService.setConfigStatusMessage('Syncing from API…', { loading: true });

  try {
    // ── Auth ────────────────────────────────────────────────────────────────
    setProgress('Checking authentication…');
    let token;
    try { token = await authService.getAccessToken(); } catch { token = null; }
    if (!token) {
      setStatus('Please sign in first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    // ── Read _Config ─────────────────────────────────────────────────────
    setProgress('Reading filter settings from _Config…');
    let cfg;
    try {
      cfg = await excelService.readConfigValues();
      await refreshSyncScopePreview();
    } catch (e) {
      setStatus('_Config sheet not found. Click "Setup Workbook" first.', 'error');
      setProgress('');
      await excelService.setConfigStatusMessage('Ready', { loading: false });
      return;
    }

    const currentYear = new Date().getFullYear();
    const startYear   = CONFIG.DATE.START_YEAR || 2020;
    let partyIds      = cfg.partyIds?.length > 0 ? cfg.partyIds : null;
    const scope       = (cfg.syncScope || 'TrialBalance').toLowerCase();
    const syncAll     = scope === 'all';

    // If no specific companies selected, resolve all party IDs from Raw_Parties table
    if (!partyIds) {
      try {
        partyIds = await excelService.readPartyIdsFromRawTable();
      } catch { /* will try to fetch from API below */ }
    }

    // ── Helpers shared with setup ─────────────────────────────────────────
    const writeTable = excelService.replaceTableData.bind(excelService);

    // ── Parties + Currencies ─────────────────────────────────────────────
    if (syncAll || scope === 'parties' || scope === 'currencies') {
      setProgress('Syncing companies & currencies…');
      await excelService.setConfigStatusMessage('Downloading companies & currencies…', { loading: true });
      try {
        const [parties, currencies] = await Promise.all([
          apiService.fetchPartiesDetail(),
          apiService.fetchCurrencies()
        ]);
        if (parties.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_PARTIES, 'tbl_RawParties',
            ['PartyId','Name','OrgNumber','CountryCode','CompanyType','EnableSync','StartDate','EndDate'],
            parties.map(p => [p.partyId??'',p.name??'',p.orgNumber??'',p.countryCode??'',p.companyType??'',p.enableSync??'',p.startDate??'',p.endDate??'']));
        }
        if (currencies.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_CURRENCIES, 'tbl_RawCurrencies',
            ['CurrencyId','Code','DisplayName','Symbol','NumericCode'],
            currencies.map(c => [c.id??'',c.code??'',c.displayName??'',c.symbol??'',c.numericCode??'']));
        }
      } catch (e) { console.warn('[Sync] parties/currencies failed:', e.message); }
    }

    // ── Exchange Rates ────────────────────────────────────────────────────
    if (syncAll || scope === 'currencies') {
      setProgress('Syncing exchange rates…');
      await excelService.setConfigStatusMessage('Downloading exchange rates…', { loading: true });
      try {
        const ratesResp = await apiService.fetchExchangeRates({ limit: 50000 });
        const rateArr = Array.isArray(ratesResp) ? ratesResp : (ratesResp.rows ?? ratesResp.items ?? []);
        if (rateArr.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_EXCHANGE_RATES, 'tbl_RawExchangeRates',
            ['FromCurrency','ToCurrency','Rate','EffectiveDate'],
            rateArr.map(r => [r.fromCurrencyCode??'',r.toCurrencyCode??'',r.rate??0,r.effectiveDate??'']));
        }
      } catch (e) { console.warn('[Sync] exchange rates failed:', e.message); }
    }

    // ── Ownership ─────────────────────────────────────────────────────────
    if (syncAll || scope === 'ownership') {
      setProgress('Syncing group ownership…');
      await excelService.setConfigStatusMessage('Downloading ownership…', { loading: true });
      try {
        const own = await apiService.fetchPartyOwnerships();
        if (own.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_OWNERSHIP, 'tbl_RawOwnership',
            ['OwnedPartyId','OwnedPartyName','OwnerPartyId','OwnerPartyName','OwnershipPct','OwnershipType','EffectiveFrom','EffectiveTo'],
            own.map(o => [o.ownedPartyId??'',o.ownedPartyName??'',o.ownerPartyId??'',o.ownerPartyName??'',o.ownershipPercentage??'',o.ownershipType??'',o.effectiveFrom??'',o.effectiveTo??'']));
        }
      } catch (e) { console.warn('[Sync] ownership failed:', e.message); }
    }

    // ── Master Chart ──────────────────────────────────────────────────────
    if (syncAll || scope === 'masterchart') {
      setProgress('Syncing master chart of accounts…');
      await excelService.setConfigStatusMessage('Downloading master chart…', { loading: true });
      try {
        const [profiles, accounts, mappings] = await Promise.all([
          apiService.fetchMasterProfiles(),
          apiService.fetchMasterAccounts(),
          apiService.fetchAccountMappings()
        ]);
        if (profiles.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_MASTER_PROFILES, 'tbl_RawMasterProfiles',
            ['ProfileId','Name'], profiles.map(p => [p.profileId??'',p.name??'']));
        }
        if (accounts.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_MASTER_ACCOUNTS, 'tbl_RawMasterAccounts',
            ['ProfileId','ProfileName','AccountId','AccountNumber','AccountName','ParentAccountId','Statement','Section','NormalBalance','CashFlowClass','IsCash','IsContra'],
            accounts.map(a => [a.profileId??'',a.profileName??'',a.accountId??'',a.accountNumber??'',a.accountName??'',a.parentAccountId??'',a.statement??'',a.section??'',a.normalBalance??'',a.cashFlowClass??'',a.isCash??false,a.isContra??false]));
        }
        if (mappings.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_ACCOUNT_MAPPINGS, 'tbl_RawAccountMappings',
            ['PartyId','ProfileId','PartyAccountNumber','PartyAccountName','MasterAccountId','MasterAccountNumber','MasterAccountName'],
            mappings.map(m => [m.partyId??'',m.profileId??'',m.partyAccountNumber??'',m.partyAccountName??'',m.masterAccountId??'',m.masterAccountNumber??'',m.masterAccountName??'']));
        }
      } catch (e) { console.warn('[Sync] master chart failed:', e.message); }
    }

    // ── Trial Balance (all years from 2020 → current) ────────────────────
    if (syncAll || scope === 'trialbalance') {
      if (!partyIds || partyIds.length === 0) {
        // Last resort: fetch parties from API
        try {
          const freshParties = await apiService.fetchPartiesDetail();
          partyIds = freshParties.map(p => p.partyId ?? p.PartyId).filter(id => id != null);
        } catch { /* ignore */ }
      }
      if (!partyIds || partyIds.length === 0) {
        setStatus('No companies found. Run "Setup Workbook" first to download company list.', 'error');
        await excelService.setConfigStatusMessage('Ready', { loading: false });
      } else {
        const allTbRows = [];
        for (let yr = startYear; yr <= currentYear; yr++) {
          setProgress(`Syncing trial balance (${yr}) for ${partyIds.length} companies…`);
          await excelService.setConfigStatusMessage(`Downloading trial balance ${yr}…`, { loading: true });
          try {
            const tbResp = await apiService.fetchTrialBalanceFlat({
              partyIds,
              year: yr,
              periodType: 12,
              period: 0,
              profileId: cfg.profileId || 1,
              includeCompanyElimination: cfg.includeCompanyElimination ?? false,
              includeHistoricalData: cfg.includeHistoricalData ?? false,
              partyAccountGrain: !(cfg.includeCompanyElimination ?? false)
            });
            const rows = Array.isArray(tbResp) ? tbResp : (tbResp.rows ?? []);
            for (const r of rows) allTbRows.push(r);
          } catch (e) {
            console.warn(`[Sync] TB year ${yr} failed:`, e.message);
          }
        }
        setProgress(`Writing trial balance (${allTbRows.length} rows)…`);
        let partyNameById = {};
        try {
          partyNameById = await excelService.readPartyNameLookupFromRawTable();
        } catch { /* ignore */ }
        if (Object.keys(partyNameById).length === 0) {
          try {
            const freshParties = await apiService.fetchPartiesDetail();
            partyNameById = buildPartyNameByIdFromApi(freshParties);
          } catch { /* ignore */ }
        }
        await writeTable(
          CONFIG.SHEETS.RAW_TRIAL_BALANCE,
          'tbl_RawTrialBalance',
          RAW_TRIAL_BALANCE_HEADERS,
          allTbRows.map((r) => mapTrialBalanceApiRow(r, partyNameById))
        );
        setStatus(`Sync complete — ${allTbRows.length} trial balance rows (${startYear}–${currentYear}).`, 'success');
      }
    }

    // ── Eliminations ──────────────────────────────────────────────────────
    if (syncAll || scope === 'eliminations') {
      setProgress(`Syncing eliminations (${startYear}–${currentYear})…`);
      await excelService.setConfigStatusMessage('Downloading eliminations…', { loading: true });
      try {
        const elim = await apiService.fetchIntercompanyEliminations(partyIds ?? [], startYear, 1, currentYear, 12);
        if (elim.length > 0) {
          await writeTable(CONFIG.SHEETS.RAW_ELIMINATIONS, 'tbl_RawEliminations',
            ['EliminationId','PartyId','PartyName','ReciprocalPartyId','ReciprocalPartyName','Year','Month','AccountNumber','Amount','CurrencyId','Status'],
            elim.map(r => [r.eliminationId??'',r.partyId??'',r.partyName??'',r.reciprocalPartyId??'',r.reciprocalPartyName??'',r.year??'',r.month??'',r.accountNumber??'',r.amount??0,r.currencyId??'',r.status??'']));
        }
      } catch (e) { console.warn('[Sync] eliminations failed:', e.message); }
    }

    // ── Update last sync timestamp ────────────────────────────────────────
    await excelService.updateLastSyncTime();
    if (!document.getElementById('setup-status').textContent) {
      setStatus('Sync complete.', 'success');
    }
    await excelService.setConfigStatusMessage('Ready', { loading: false });
    await refreshSyncScopePreview();

  } catch (err) {
    console.error('[SyncData]', err);
    setStatus(`Error: ${err.message}`, 'error');
    await excelService.setConfigStatusMessage('Error — see task pane', { loading: false });
  } finally {
    if (btn) btn.disabled = false;
    setProgress('');
  }
}

// ── Navigate to _Config ───────────────────────────────────────────────────────

async function handleOpenConfigSheet() {
  const ok = await navigateToConfig();
  if (!ok) setStatus('Run "Setup Workbook" first to create the _Config sheet.', 'error');
  else {
    try {
      await excelService.refreshConfigDerivedValidation();
    } catch (e) {
      console.warn('[OpenConfig] refresh dropdowns:', e.message);
    }
    refreshSyncScopePreview().catch(() => {});
  }
}

async function navigateToConfig() {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(CONFIG.SHEETS.CONFIG_SHEET);
      sheet.load('isNullObject');
      await context.sync();
      if (sheet.isNullObject) throw new Error('_Config sheet not found');
      sheet.activate();
      await context.sync();
    });
    return true;
  } catch {
    return false;
  }
}

// ── UI helpers ────────────────────────────────────────────────────────────────

function setStatus(msg, type) {
  const el = document.getElementById('setup-status');
  if (!el) return;
  el.textContent = msg;
  el.className   = type || '';
}

function setProgress(msg) {
  const el = document.getElementById('sync-progress');
  if (!el) return;
  if (msg) { el.textContent = '⟳  ' + msg; el.style.display = 'block'; }
  else      { el.textContent = '';           el.style.display = 'none';  }
}

// ── AI dashboard chat (workbook → API → apply plan on new sheet) ─────────────

/** Shown on the AI assistance tab: whether Raw_* tables exist before the user sends a prompt. */
async function updateAiAssistanceReadinessBanner() {
  const wrap = document.getElementById('ai-readiness-wrap');
  const textEl = document.getElementById('ai-readiness-text');
  if (!wrap || !textEl) return;
  textEl.textContent = 'Checking workbook for Raw_* tables…';
  wrap.classList.remove('ok', 'warn');
  wrap.classList.add('warn');
  try {
    const { workbookTables, rawSheetsMissingExcelTable } = await collectWorkbookTablesForAi({});
    const n = workbookTables?.length ?? 0;
    if (n === 0) {
      textEl.textContent =
        
        'No Raw_* Excel tables found yet. Click Get latest data before you start (or switch to Lite / Advanced to set up the workbook).';
      wrap.classList.remove('ok');
      wrap.classList.add('warn');
      if (rawSheetsMissingExcelTable?.length) {
        textEl.textContent += ` Raw sheets without a table: ${rawSheetsMissingExcelTable.join(', ')} — use Insert → Table.`;
      }
      return;
    }
    textEl.textContent = `Ready: ${n} Raw_* table(s) will be sent with your next message. You can still use Get latest data to refresh numbers from Corvenia.`;
    wrap.classList.remove('warn');
    wrap.classList.add('ok');
  } catch (e) {
    textEl.textContent = `Could not read the workbook (${e.message || e}). Open a workbook and try again.`;
    wrap.classList.remove('ok');
    wrap.classList.add('warn');
  }
}

function setAiChatStatus(msg) {
  const el = document.getElementById('ai-chat-status');
  if (!el) return;
  el.textContent = msg || '';
}

function appendAiChatBubble(kind, text) {
  const box = document.getElementById('ai-chat-messages');
  if (!box) return;
  const div = document.createElement('div');
  div.className = `ai-msg ${kind === 'user' ? 'user' : kind === 'err' ? 'err' : 'bot'}`;
  div.textContent = text;
  box.appendChild(div);
  box.scrollTop = box.scrollHeight;
}

async function handleAiDashboardChatSend() {
  const input = document.getElementById('ai-chat-input');
  const btn = document.getElementById('ai-chat-send');
  const text = (input?.value || '').trim();
  if (!text || !btn) return;

  btn.disabled = true;
  setAiChatStatus('');
  appendAiChatBubble('user', text);
  if (input) input.value = '';

  try {
    const auth = await authService.checkAuthenticationState();
    if (!auth.isAuthenticated) {
      appendAiChatBubble('err', 'Sign in with Microsoft first.');
      return;
    }

    setAiChatStatus('Reading workbook tables…');
    const {
      workbookTables,
      rawSheetsMissingExcelTable,
      totalListObjectsInWorkbook,
    } = await collectWorkbookTablesForAi({});
    if (!workbookTables.length) {
      appendAiChatBubble(
        'err',
        'No Raw_* tables found yet. Click "Get latest data" on the AI assistance tab (or use Lite → Get latest data / Advanced → Setup), then send your message again.'
      );
      setAiChatStatus('');
      updateAiAssistanceReadinessBanner().catch(() => {});
      return;
    }

    const workbookDiagnostics = rawSheetsMissingExcelTable?.length
      ? { rawSheetsWithoutExcelTable: rawSheetsMissingExcelTable }
      : undefined;

    const tableHint =
      typeof totalListObjectsInWorkbook === 'number'
        ? ` — ${workbookTables.length} Raw_* table(s) for AI (${totalListObjectsInWorkbook} ListObject(s) total in workbook; Rpt_ and others omitted)`
        : '';
    setAiChatStatus(
      `Calling Corvenia API (AIChat)…${tableHint}${
        rawSheetsMissingExcelTable?.length
          ? ` — Raw_* sheets with no table yet: ${rawSheetsMissingExcelTable.join(', ')} (Insert → Table to include them).`
          : ''
      }`
    );

    const forceNewSheet = userWantsExplicitNewSheet(text);
    let userMessageForApi = text;
    if (forceNewSheet) {
      const avoid = lastAiDashboardSheetName ? ` Avoid reusing "${lastAiDashboardSheetName}" unless that name is unused.` : '';
      userMessageForApi += `\n\n[Add-in: user asked for a NEW worksheet (not an in-place update).${avoid} Use a distinct dashboard.sheetName; the add-in will create a new tab.]`;
    } else if (lastAiDashboardSheetName) {
      userMessageForApi += `\n\n[Add-in: this chat last built the dashboard on worksheet "${lastAiDashboardSheetName}". For changes or improvements, set dashboard.sheetName to exactly "${lastAiDashboardSheetName}" so that sheet is updated in place.]`;
    }

    const resp = await apiService.requestExcelDashboardPlan({
      userMessage: userMessageForApi,
      workbookTables,
      workbookDiagnostics,
      conversationHistory: aiDashboardChatHistory,
    });

    setAiChatStatus('');

    let assistantText = String(resp.assistantMessage || '').trim();
    if (!assistantText && resp.rawModelText) {
      assistantText = 'Could not parse the model reply. Check server logs and AIChat configuration.';
    }

    if (resp.dashboard) {
      try {
        const applied = await applyDashboardSpec({
          ...resp.dashboard,
          forceNewSheet,
        });
        lastAiDashboardSheetName = applied.sheetName;
        const w = (applied.warnings || []).filter(Boolean);
        assistantText += w.length ? `\n\n${w.join('\n')}` : '';
        assistantText += applied.reusedExisting
          ? `\n\nUpdated sheet: ${applied.sheetName}`
          : `\n\nNew sheet: ${applied.sheetName}`;
      } catch (buildErr) {
        console.error('[AI dashboard build]', buildErr);
        const loc = buildErr.debugInfo?.errorLocation || buildErr.code;
        assistantText += `\n\nCould not build the sheet: ${buildErr.message || buildErr}${
          loc ? ` (${loc})` : ''
        }`;
      }
    }

    appendAiChatBubble('bot', assistantText || 'No response.');

    aiDashboardChatHistory.push({ isUser: true, content: text });
    aiDashboardChatHistory.push({ isUser: false, content: assistantText || 'No response.' });
    while (aiDashboardChatHistory.length > AI_DASHBOARD_HISTORY_MAX) {
      aiDashboardChatHistory.splice(0, aiDashboardChatHistory.length - AI_DASHBOARD_HISTORY_MAX);
    }
  } catch (err) {
    console.error('[AI dashboard chat]', err);
    setAiChatStatus('');
    appendAiChatBubble('err', err.message || 'Request failed.');
  } finally {
    btn.disabled = false;
  }
}
